import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import {
  adminApi,
  type Application,
  type CreateAgentInput,
  type CreatedAgent,
  type CredentialGrant,
} from '../lib/api.js';
import { useAction } from '../lib/useAsync.js';
import { useSession } from '../lib/auth-client.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { KeyReveal } from '../components/KeyReveal.js';
import { Badge, Button, Field, Note, OutcomeNote, inputStyle } from '../components/ui.js';
import {
  GRANTS,
  MAX_EXPIRY_DAYS,
  MAX_NAME_LENGTH,
  parseExpiry,
  useIssuableRoles,
} from './IssueCredential.js';

/**
 * Create an agent: the account, its role and its credential, in one act.
 *
 * ── WHY THIS IS NOT THREE SCREENS ───────────────────────────────────
 *
 * An agent is not usable until all three exist, and a portal doing them in
 * sequence fails in the middle — leaving an account with no role, or a role
 * with no key, each of which looks like a working agent in a list and is not.
 * `POST /admin/agents` does all three (RidgeWill/auth#538) and this is the one
 * form that feeds it.
 *
 * ── THE ADDRESS IS A LABEL ──────────────────────────────────────────
 *
 * An agent cannot sign in and cannot be recovered through its address
 * (RidgeWill/auth#539), so unlike every other account on this platform the
 * mailbox is not a way in. That is said on the form, because an administrator
 * who believes otherwise will choose the address as though it were a
 * credential — and the previous version of this feature, where it WAS one, is
 * recent enough to be the assumption people arrive with.
 *
 * ── AND IT NEVER CLOSES ON A FAILURE ────────────────────────────────
 *
 * The same trap `AddUser` carries a note about, and worse here: the account can
 * be created while the credential is not, and the key is readable exactly once.
 * A modal that dismissed itself on that path would destroy the only copy.
 */
export function AddAgent({
  applications,
  onClose,
  onCreated,
  reload,
}: {
  applications: Application[];
  onClose: () => void;
  onCreated: () => void;
  /** Refresh the list WITHOUT closing, for the half-success above: the row must
   *  appear even though this dialog stays open to explain what did not. */
  reload: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [withCredential, setWithCredential] = useState(true);
  const [credentialName, setCredentialName] = useState('');
  const [appSlug, setAppSlug] = useState('');
  const [grants, setGrants] = useState<CredentialGrant[]>([]);
  const [expiry, setExpiry] = useState('90');
  const [created, setCreated] = useState<CreatedAgent | null>(null);
  const action = useAction();
  const { data: session } = useSession();

  /** Only roles the signed-in administrator holds, or every role for a Global
   *  Admin: the service refuses any other, so offering more is a form whose
   *  options the server rejects. */
  const roles = useIssuableRoles(session?.user.role);
  const { days, valid: expiryValid } = parseExpiry(expiry);

  const ready =
    name.trim() !== '' &&
    email.trim() !== '' &&
    (!withCredential || (credentialName.trim() !== '' && appSlug !== '' && grants.length > 0)) &&
    expiryValid;

  /**
   * Once the agent exists, the form is gone and only the outcome remains.
   *
   * Not a panel below the form: leaving the fields on screen invites a second
   * submit, and a second submit would try to create the same account again
   * rather than failing usefully.
   */
  if (created) {
    return (
      <Modal
        title="Agent created"
        subtitle={
          created.credential
            ? 'Copy the credential now. This is the only time it is readable.'
            : 'The account exists.'
        }
        onClose={() => {
          onCreated();
          onClose();
        }}
        footer={
          <ModalActions>
            <Button
              onClick={() => {
                onCreated();
                onClose();
              }}
            >
              Done
            </Button>
          </ModalActions>
        }
      >
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginBottom: '.9rem' }}>
          <Badge tone="warn">agent</Badge>
          {created.role ? <Badge tone="warn">{created.role}</Badge> : null}
          <Badge>{created.email}</Badge>
        </div>

        {created.credentialError ? (
          /**
           * The half-success, said plainly. The account is real and is in the
           * list; only the key is missing, and it can be issued from the
           * credentials screen. Silence here would leave an agent that looks
           * finished and cannot act.
           */
          <Note tone="danger">
            The account was created, but its credential was not:{' '}
            {created.credentialError.message} Issue one from Credentials when you are ready. The
            agent exists and needs no second attempt here.
          </Note>
        ) : null}

        {created.credential ? <KeyReveal value={created.credential.key} /> : null}

        <Note>
          This account cannot sign in and cannot be recovered through its address. To stop the agent
          acting, revoke it from its page under Agents.
        </Note>
      </Modal>
    );
  }

  return (
    <Modal
      title="Add an agent"
      subtitle="An account the platform runs, rather than somebody who signs in."
      onClose={onClose}
      width={560}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={!ready || action.running}
            onClick={() => {
              /**
               * CLOSES ONLY ON A REAL SUCCESS, the same fix `AddUser` carries
               * a note about. `run` catches what the body throws and RESOLVES,
               * so a `.then(onCreated)` would fire on every path — and here it
               * would unmount the one and only reveal of the key.
               */
              let ok = false;
              void action
                .run(async () => {
                  const input: CreateAgentInput = {
                    name: name.trim(),
                    email: email.trim(),
                    ...(role ? { role } : {}),
                    ...(withCredential
                      ? {
                          credential: {
                            name: credentialName.trim(),
                            appSlug,
                            grants,
                            ...(days === undefined ? {} : { expiresInDays: days }),
                          },
                        }
                      : {}),
                  };
                  const result = await adminApi.createAgent(input);
                  setCreated(result);
                  ok = true;
                  /* The row exists whichever half failed, so the list behind
                     this dialog must show it. */
                  reload();
                  return `Created ${result.email}.`;
                })
                .then(() => {
                  if (!ok) return;
                });
            }}
          >
            {action.running ? 'Creating…' : 'Create agent'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem', marginTop: '.4rem' }}>
        <Field
          label="Name"
          hint="What this agent is for, in words: “Claude support triage”. It is what the Agents list shows."
        >
          <input
            style={inputStyle}
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Claude support triage"
          />
        </Field>

        <Field
          label="Email"
          hint="Identifies the account, and nothing more. An agent cannot sign in and cannot be recovered through this address, so it does not need to be a mailbox anybody reads."
        >
          <input
            style={inputStyle}
            value={email}
            maxLength={254}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="claude-support@ridgewill.com"
          />
        </Field>

        <Field
          label="Acts with the role"
          hint="What this agent may do, exactly as for a person. Only roles you hold are offered, because the auth service refuses any other. Only a Global Admin can give an agent Global Admin."
        >
          <Select
            block
            label="Acts with the role"
            value={role}
            onChange={setRole}
            options={[{ value: '', label: 'No role' }, ...roles.map((r) => ({ value: r, label: r }))]}
          />
        </Field>

        {role !== '' && (
          <Note tone="danger">
            This agent can do anything the <code>{role}</code> role can, with no sign-in and no
            second factor.
          </Note>
        )}

        <label style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontSize: '.85rem' }}>
          <input
            type="checkbox"
            checked={withCredential}
            style={{ marginTop: '.2rem' }}
            onChange={(e) => setWithCredential(e.target.checked)}
          />
          <span>
            <strong>Issue a service credential now</strong>
            <div style={{ color: C.muted, fontSize: '.78rem', lineHeight: 1.4 }}>
              The key it authenticates with. An agent without one is a real state, and you can issue
              it later from Credentials.
            </div>
          </span>
        </label>

        {withCredential && (
          <>
            <Field
              label="Credential name"
              hint={`Who holds it, in words. Up to ${MAX_NAME_LENGTH} characters.`}
            >
              <input
                style={inputStyle}
                value={credentialName}
                maxLength={MAX_NAME_LENGTH}
                onChange={(e) => setCredentialName(e.target.value)}
                placeholder="claude agent (dev)"
              />
            </Field>

            <Field
              label="Application"
              hint="Whose users this key may see. The auth service scopes every lookup to this app, and a user outside it is reported as absent."
            >
              <Select
                block
                label="Application"
                value={appSlug}
                onChange={setAppSlug}
                options={[
                  { value: '', label: 'Choose an application…' },
                  ...applications.map((a) => ({ value: a.slug, label: `${a.name} (${a.slug})` })),
                ]}
              />
            </Field>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
              <span style={{ fontWeight: 600, color: C.textMid, fontSize: '.85rem' }}>
                Capabilities
              </span>
              {GRANTS.map((g) => (
                <label
                  key={g.value}
                  style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontSize: '.85rem' }}
                >
                  <input
                    type="checkbox"
                    checked={grants.includes(g.value)}
                    style={{ marginTop: '.2rem' }}
                    onChange={(e) =>
                      setGrants((cur) =>
                        e.target.checked ? [...cur, g.value] : cur.filter((v) => v !== g.value),
                      )
                    }
                  />
                  <span>
                    <strong>{g.label}</strong>
                    <code style={{ color: C.muted, marginLeft: '.4rem', fontSize: '.78rem' }}>
                      {g.value}
                    </code>
                    <div style={{ color: C.muted, fontSize: '.78rem', lineHeight: 1.4 }}>{g.hint}</div>
                  </span>
                </label>
              ))}
            </div>

            <Field
              label="Expires after (days)"
              hint={`Up to ${MAX_EXPIRY_DAYS} days. Leave blank for a key that never expires: a real choice, and one nothing will ever prompt anyone to revisit.`}
            >
              <input
                style={inputStyle}
                value={expiry}
                inputMode="numeric"
                placeholder="blank = never"
                onChange={(e) => setExpiry(e.target.value)}
              />
            </Field>

            {!expiryValid && (
              <Note tone="danger">Expiry must be a whole number of days, 1–{MAX_EXPIRY_DAYS}.</Note>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
